import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Heart, MessageCircle, Send, LogOut, Users, Crown, Lock, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function CommunityDetailPage() {
  const { communityId } = useParams();
  const { user } = useAuth();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState(null);
  const [posting, setPosting] = useState(false);
  const [commentText, setCommentText] = useState({});
  const fileRef = useRef(null);

  useEffect(() => { loadCommunity(); loadPosts(); }, [communityId]);

  const loadCommunity = async () => {
    try { const res = await api.get(`/communities/${communityId}`); setCommunity(res.data); }
    catch { toast.error('Community not found'); }
  };

  const loadPosts = async () => {
    try { const res = await api.get(`/communities/${communityId}/posts`); setPosts(res.data); }
    catch {}
  };

  const isMember = community?.members?.includes(user?.id);

  const handlePost = async () => {
    if (!content.trim() && !media) return;
    setPosting(true);
    try {
      await api.post('/posts', { content: content.trim(), media: media?.data || null, media_type: media?.type || null, community_id: communityId });
      setContent(''); setMedia(null);
      loadPosts();
      toast.success('Posted');
    } catch { toast.error('Failed to post'); }
    finally { setPosting(false); }
  };

  const handleLeave = async () => {
    try { await api.post(`/communities/${communityId}/leave`); loadCommunity(); toast.success('Left community'); }
    catch { toast.error('Failed'); }
  };

  const toggleLike = async (postId) => {
    try { const res = await api.post(`/posts/${postId}/like`); setPosts(p => p.map(x => x.id === postId ? res.data : x)); }
    catch {}
  };

  const addComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    try {
      const res = await api.post(`/posts/${postId}/comment`, { content: text });
      setPosts(p => p.map(x => x.id === postId ? res.data : x));
      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch {}
  };

  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMedia({ data: reader.result.split(',')[1], type: file.type.startsWith('video') ? 'video' : 'image' });
    reader.readAsDataURL(file);
  };

  if (!community) return <div className="text-center py-20 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="community-detail-page">
      {/* Header */}
      <div className="border border-border/40 p-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-serif text-2xl">{community.name}</h1>
              {community.is_private && <Lock size={14} className="text-muted-foreground" />}
              {community.is_paid && <Badge className="bg-gold text-obsidian rounded-sm text-[10px] uppercase"><Crown size={8} className="mr-1" /> {community.price?.toFixed(2)}/mo</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mb-2">{community.description}</p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Users size={12} /> {community.members?.length || 0} members</span>
              <span>Created by {community.creator_name}</span>
            </div>
          </div>
          {isMember && community.creator_id !== user?.id && (
            <Button data-testid="leave-community-btn" variant="outline" size="sm" onClick={handleLeave} className="rounded-sm text-xs uppercase tracking-wider">
              <LogOut size={12} className="mr-1" /> Leave
            </Button>
          )}
        </div>
      </div>

      {/* Post Creation (members only) */}
      {isMember && (
        <div className="border border-border/40 p-4 mb-8" data-testid="community-post-form">
          <Textarea
            data-testid="community-post-input"
            placeholder="Share with this community..."
            value={content}
            onChange={e => setContent(e.target.value)}
            className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 resize-none min-h-[60px] text-sm"
          />
          {media && (
            <div className="mt-2 relative inline-block">
              <img src={`data:image/jpeg;base64,${media.data}`} alt="" className="max-h-32 rounded-sm" />
              <button onClick={() => setMedia(null)} className="absolute -top-2 -right-2 bg-obsidian text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
            </div>
          )}
          <div className="flex justify-between mt-3">
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleMediaSelect} />
              <Button data-testid="community-add-media" variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => fileRef.current?.click()}>
                <ImageIcon size={14} className="mr-1" /> Media
              </Button>
            </div>
            <Button data-testid="community-post-submit" disabled={posting || (!content.trim() && !media)} onClick={handlePost} className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
              <Send size={14} className="mr-1" /> Post
            </Button>
          </div>
        </div>
      )}

      {!isMember && (
        <div className="text-center py-12 border border-border/40 mb-8">
          <p className="text-muted-foreground text-sm mb-4">Join this community to participate in discussions.</p>
        </div>
      )}

      {/* Posts */}
      <div className="space-y-6">
        {posts.map(post => (
          <article key={post.id} className="border border-border/40 hover:border-gold/30 transition-colors duration-500" data-testid={`community-post-${post.id}`}>
            <div className="p-4">
              <Link to={`/profile/${post.author_id}`} className="flex items-center gap-2 mb-3 hover:opacity-80">
                <Avatar className="h-7 w-7 border"><AvatarFallback className="bg-obsidian text-white text-[10px]">{(post.author_name || 'U')[0]}</AvatarFallback></Avatar>
                <span className="text-sm font-medium">{post.author_name}</span>
                <span className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString()}</span>
              </Link>
              <p className="text-sm leading-relaxed">{post.content}</p>
              {post.media && post.media_type === 'image' && <img src={`data:image/jpeg;base64,${post.media}`} alt="" className="mt-3 w-full max-h-72 object-cover img-art" />}
            </div>
            <div className="px-4 pb-2 flex items-center gap-4 border-t border-border/20 pt-2">
              <button onClick={() => toggleLike(post.id)} className="flex items-center gap-1 text-sm hover:text-gold transition-colors" data-testid={`like-cp-${post.id}`}>
                <Heart size={14} className={post.likes?.includes(user?.id) ? 'fill-gold text-gold' : ''} /> <span className="text-xs">{post.likes?.length || 0}</span>
              </button>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><MessageCircle size={14} /> {post.comments?.length || 0}</span>
            </div>
            {post.comments?.length > 0 && (
              <div className="px-4 pb-2 space-y-1">
                {post.comments.slice(-2).map(c => (
                  <div key={c.id} className="text-xs"><span className="font-medium">{c.author_name}</span> <span className="text-muted-foreground">{c.content}</span></div>
                ))}
              </div>
            )}
            {isMember && (
              <div className="px-4 pb-3 flex gap-2">
                <input data-testid={`comment-cp-${post.id}`} placeholder="Comment..." value={commentText[post.id] || ''} onChange={e => setCommentText(p => ({ ...p, [post.id]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addComment(post.id)} className="flex-1 text-xs border-0 border-b border-input bg-transparent py-1 focus:outline-none focus:border-obsidian" />
                <button onClick={() => addComment(post.id)} className="text-xs text-gold font-medium uppercase tracking-wider">Post</button>
              </div>
            )}
          </article>
        ))}
        {posts.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">No posts in this community yet.</p>}
      </div>
    </div>
  );
}
